import { useState, useCallback, useMemo } from "react";
import { useMarkers } from "../hooks/useMarkers";
import type { Marker, MarkerInput } from "../api/schedulerApi";
import "./scheduler.css";

/* ── Constants ────────────────────────────────────────────────────── */

const MARKER_TYPES = [
  { value: "deployment", label: "Deployment" },
  { value: "incident",   label: "Incident" },
  { value: "shift",      label: "Shift" },
  { value: "milestone",  label: "Milestone" },
  { value: "custom",     label: "Custom" },
] as const;

const TYPE_ICONS: Record<string, string> = {
  deployment: "\uD83D\uDE80",
  incident:   "\u26A0\uFE0F",
  shift:      "\uD83D\uDD04",
  milestone:  "\uD83C\uDFC1",
  custom:     "\uD83D\uDCCC",
};

/* ── Component ────────────────────────────────────────────────────── */

export default function MarkerList() {
  const { markers, loading, saving, saveMarker, removeMarker } = useMarkers();
  const [editing, setEditing] = useState<Marker | "new" | null>(null);

  const sorted = useMemo(
    () => [...markers].sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || "")),
    [markers],
  );

  return (
    <div className="mkr-root">
      <div className="mkr-toolbar">
        <span className="mkr-toolbar__count">
          {markers.length} marker{markers.length !== 1 ? "s" : ""}
          {loading && <span className="sch-loading-dot" />}
        </span>
        <button
          type="button"
          className="mkr-add-btn"
          onClick={() => setEditing("new")}
        >
          + Add Marker
        </button>
      </div>

      {sorted.length === 0 && !loading && (
        <div className="sch-placeholder">
          <span className="sch-placeholder__icon">&#x1F4CD;</span>
          <span>No markers yet — add one to mark a deployment, incident, or milestone</span>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="mkr-table-wrap">
          <table className="mkr-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Label</th>
                <th>Timestamp</th>
                <th>Source</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => (
                <tr key={m.id}>
                  <td>
                    <span className="mkr-type-badge" style={{ borderColor: m.color }}>
                      {TYPE_ICONS[m.marker_type] || TYPE_ICONS.custom}{" "}
                      {m.marker_type}
                    </span>
                  </td>
                  <td className="mkr-label-cell">
                    <span className="mkr-color-dot" style={{ background: m.color }} />
                    {m.label}
                  </td>
                  <td className="mkr-ts-cell">{formatTimestamp(m.timestamp)}</td>
                  <td className="mkr-source-cell">{m.source}</td>
                  <td className="mkr-actions-cell">
                    <button
                      type="button"
                      className="mkr-edit-btn"
                      onClick={() => setEditing(m)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="mkr-del-btn"
                      onClick={() => removeMarker(m.id)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <MarkerEditor
          marker={editing === "new" ? null : editing}
          saving={saving}
          onSave={(input) => {
            saveMarker(input);
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ── Marker Editor ────────────────────────────────────────────────── */

function MarkerEditor({
  marker,
  saving,
  onSave,
  onClose,
}: {
  marker: Marker | null;
  saving: boolean;
  onSave: (input: MarkerInput) => void;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(marker?.label ?? "");
  const [markerType, setMarkerType] = useState(marker?.marker_type ?? "custom");
  const [date, setDate] = useState(
    marker?.timestamp ? marker.timestamp.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [time, setTime] = useState(
    marker?.timestamp ? marker.timestamp.slice(11, 16) : "12:00",
  );
  const [notes, setNotes] = useState(marker?.notes ?? "");

  const isValid = label.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(date);

  const handleSave = useCallback(() => {
    if (!isValid) return;
    const timestamp = `${date}T${time}:00Z`;
    onSave({
      id: marker?.id,
      label: label.trim(),
      timestamp,
      marker_type: markerType,
      notes,
    });
  }, [marker, label, date, time, markerType, notes, isValid, onSave]);

  return (
    <div className="sch-editor-overlay" onClick={onClose}>
      <div className="sch-editor" onClick={(e) => e.stopPropagation()}>
        <div className="sch-editor__header">
          <span className="sch-editor__badge" style={{ background: marker?.color ?? "#f87171" }} />
          <span className="sch-editor__title-label">
            {marker ? "Edit Marker" : "New Marker"}
          </span>
        </div>

        <input
          className="sch-editor__input"
          placeholder="Marker label..."
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && isValid) handleSave();
            if (e.key === "Escape") onClose();
          }}
        />

        <div className="sch-editor__row">
          <label className="sch-editor__label">Type</label>
          <select
            className="sch-editor__select"
            value={markerType}
            onChange={(e) => setMarkerType(e.target.value as Marker['marker_type'])}
          >
            {MARKER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sch-editor__row sch-editor__time-row">
          <div className="sch-editor__time-field">
            <label className="sch-editor__label">Date</label>
            <input
              type="date"
              className="sch-editor__input sch-editor__date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <span className="sch-editor__time-sep">@</span>
          <div className="sch-editor__time-field">
            <label className="sch-editor__label">Time</label>
            <input
              type="time"
              className="sch-editor__input sch-editor__date-input"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
        </div>

        <textarea
          className="sch-editor__input sch-editor__notes"
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />

        <div className="sch-editor__actions">
          <button
            type="button"
            className="sch-editor__save"
            disabled={!isValid || saving}
            onClick={handleSave}
          >
            {saving ? "Saving..." : marker ? "Update" : "Save"}
          </button>
          <button type="button" className="sch-editor__cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────── */

function formatTimestamp(ts: string): string {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
